import { HTMLAttributes } from 'react'
import Link from 'next/link'

import Container from '@/components/base/container'
import CtaButton from '@/components/base/cta-button'
import Motion from '@/components/base/motion'
import Section from '@/components/base/section'
import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'
import { mediaCenterRevealAnimation } from '@/components/sections/animations/media-center'
import Spacer from '@/components/sections/spacer'
import { useStore } from '@/contexts/store'
import { formatDate } from '@/utils/date'

import { cn } from '@/lib/utils'

type MediaCenterItem = {
  id?: number | string
  title?: string
  category?: {
    title?: string
    link?: string
  }[]
  date?: string
  download?: {
    url?: string
    link?: string
  }
}

export type MediaCenterProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    collection: MediaCenterItem[]
    visible: boolean
  }>

const MediaCenter = ({ collection = [], className }: MediaCenterProps) => {
  const { strings } = useStore()

  const pretitle = strings?.labels?.media_center_pretitle || 'Downloads'
  const title = strings?.labels?.media_center_title || 'From Media Center'
  const link = strings?.labels?.media_center_link || {
    title: 'All Media',
    url: '/media-center',
    target: '_self',
    classes: ''
  }

  return (
    <Motion animation={mediaCenterRevealAnimation}>
      {(motionRef) => (
        <Section ref={motionRef} className={cn(className)}>
          <Spacer variant="xxl" />
          <Container>
            <div className="flex flex-col gap-spacing-lg">
              <div className="flex flex-col gap-spacing-xs">
                {pretitle ? (
                  <Typography
                    variant="small"
                    className="condensed text-text-primary-light dark:text-text-primary-dark"
                    data-motion-pretitle
                  >
                    {pretitle}
                  </Typography>
                ) : null}

                {title ? (
                  <Typography
                    variant="h6"
                    component="h2"
                    className="text-h6 text-text-heading-light md:text-d-h6 dark:text-text-heading-dark"
                    data-motion-title
                  >
                    {title}
                  </Typography>
                ) : null}
              </div>

              {collection && collection.length > 0 ? (
                <div className="flex flex-col gap-spacing-2xs">
                  {collection.map((item, index) => {
                    return (
                      <Link
                        key={`media-center-item-${index}`}
                        href={item.download?.url || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        scroll={false}
                        className={cn(
                          'group',
                          'grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-spacing-sm border-b border-border-secondary-light py-spacing-2xs dark:border-border-secondary-dark'
                        )}
                        data-motion-item
                      >
                        <Typography
                          variant="regular"
                          className="text-text-primary-light dark:text-text-primary-dark"
                        >
                          {item?.title}
                        </Typography>

                        {/*  <Typography
                          variant="regular"
                          className="text-text-primary-light dark:text-text-primary-dark"
                        >
                          {item?.category?.length
                            ? `— ${item.category.map((cat) => cat.title).join(', ')}`
                            : ''}
                        </Typography> */}

                        {item.date ? (
                          <Typography
                            variant="small"
                            className="condensed whitespace-nowrap text-text-primary-light dark:text-text-primary-dark"
                          >
                            {formatDate(item.date)}
                          </Typography>
                        ) : null}

                        <SVGIcon
                          symbol="cta-arrow"
                          className={cn(
                            'size-[16px] shrink-0 text-text-primary-light dark:text-text-primary-dark',
                            'transition-transform duration-300 ease-out-cubic',
                            'group-hover:rotate-45'
                          )}
                          aria-hidden="true"
                        />
                      </Link>
                    )
                  })}
                </div>
              ) : null}

              {link?.url ? (
                <CtaButton
                  href={link.url}
                  target={link.target || '_self'}
                  variant="secondary"
                  typography="regular"
                  className="condensed place-self-start"
                  data-motion-cta
                >
                  {link.title}
                </CtaButton>
              ) : null}
            </div>
          </Container>
          <Spacer variant="xxl" />
        </Section>
      )}
    </Motion>
  )
}

export default MediaCenter
